import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Link, Redirect, useHistory, useLocation } from "react-router-dom";
import { TbExternalLink, TbShield } from "react-icons/tb";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasInternalTransferOptions } from "../../../../Hooks/useBaasInternalTransferOptions";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { useBaasPlatformCreateAccess } from "../../../../Hooks/useBaasPlatformCreateAccess";
import { BaasTablePageSkeleton } from "../components/BaasSkeleton";
import { hasBaasBankingRails, isBaasCardsOnlyInstance } from "../baasCapabilities";
import { buildEntidadeDetailPath } from "../Entidades/BaasEntidadeDetailPage";
import {
  BaasEntityVirtualAccountsPanel,
} from "../Accounts/components/BaasEntityVirtualAccountsPanel";
import { BaasAccountDetailPanel } from "../Accounts/components/BaasAccountDetailPanel";
import { BaasFundShellsButton } from "../Accounts/components/BaasFundShellsButton";
import { fetchBaasEntityById } from "../../../../Services/baasEntidadesApi";
import {
  fetchBaasInstanceDetail,
  isBaasProdAwaitingLiberation,
} from "../../../../Services/baasInstancesApi";
import { formatBaasInstanceCredentialError } from "../../../../Services/baasConsoleClient";
import { resolveBaasInstanceOwnerEntityId } from "../../../../Services/baasVirtualShells";
import { BaasProdAwaitingHomologBanner } from "../Instances/components/BaasProdAwaitingHomologBanner";
import { BAAS_ROOT_PATH } from "../constants";
import {
  isRootPageOperationShell,
  pickDefaultRootShellId,
  resolveRootPageRootAccountId,
} from "./baasRootShellPick";

const formatDocument = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 14) {
    return digits.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5",
    );
  }
  if (digits.length === 11) {
    return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
  }
  return String(value || "").trim() || "—";
};

export const BaasRootPage = () => {
  // INFRA lastro of the *pinned* instance only. On VALUT-GLOBAL that is Valut
  // shells — peer Roots never appear here (see baasRootShellPick / peer-roots on Contas).
  const history = useHistory();
  const location = useLocation();
  const { selectedInstance, dataEpoch, instances, clearPeerOpsPin } = useBaasInstance();

  // Soft peer ops must not leave Root showing client INFRA — clear override on enter.
  useEffect(() => {
    clearPeerOpsPin?.();
  }, [clearPeerOpsPin]);
  const { can, capabilities, loading: capabilitiesLoading } = useBaasCapabilities();
  const { isValutConfigAdmin } = useBaasPlatformCreateAccess();
  const canTransfer = can("accounts.transfer");
  const canViewAccounts = can("accounts.view");
  const canViewEntities =
    can("entities.list") || can("entities.create") || can("entities.update");
  const isLoading = useBaasPageLoading(
    selectedInstance?.id ? `${selectedInstance.id}:${dataEpoch}` : null,
  );
  const {
    loading: transferOptionsLoading,
    error: transferOptionsError,
    sourceAccounts: transferSourceAccounts,
    destinationAccounts: transferDestinationAccounts,
    virtualAccounts: transferVirtualAccounts,
  } = useBaasInternalTransferOptions();

  const [ownerEntityId, setOwnerEntityId] = useState(() =>
    resolveBaasInstanceOwnerEntityId(selectedInstance),
  );
  const [ownerEntity, setOwnerEntity] = useState(null);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [ownerError, setOwnerError] = useState(null);
  const [selectedShellId, setSelectedShellId] = useState("");

  useEffect(() => {
    let cancelled = false;

    if (!selectedInstance) {
      setOwnerEntityId("");
      setOwnerEntity(null);
      setOwnerError(null);
      return undefined;
    }

    // Always refresh from instance detail so legacy PERSON OwnerEntityId can be
    // healed to BUSINESS (enti_) on the Baas GET /v1/instances/{id} path.
    const fromSelection = resolveBaasInstanceOwnerEntityId(selectedInstance);
    if (fromSelection) {
      setOwnerEntityId(fromSelection);
    }

    (async () => {
      try {
        const detail = await fetchBaasInstanceDetail(selectedInstance);
        if (cancelled) return;
        const fromDetail = resolveBaasInstanceOwnerEntityId(detail);
        if (fromDetail) setOwnerEntityId(fromDetail);
        else if (!fromSelection) setOwnerEntityId("");
      } catch {
        if (!cancelled && !fromSelection) setOwnerEntityId("");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance, dataEpoch]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedInstance || !ownerEntityId) {
      setOwnerEntity(null);
      setOwnerError(null);
      setOwnerLoading(false);
      return undefined;
    }

    setOwnerLoading(true);
    setOwnerError(null);
    (async () => {
      try {
        const next = await fetchBaasEntityById(selectedInstance, ownerEntityId);
        if (!cancelled) {
          setOwnerEntity(next);
          if (!next) {
            setOwnerError(intl.get("baas.root.owner.notFound"));
          }
        }
      } catch (error) {
        if (!cancelled) {
          setOwnerEntity(null);
          const friendly = formatBaasInstanceCredentialError(error);
          setOwnerError(
            friendly?.detail
              || friendly?.message
              || error?.detail
              || error?.message
              || intl.get("baas.root.owner.loadFailed"),
          );
        }
      } finally {
        if (!cancelled) setOwnerLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance, ownerEntityId, dataEpoch]);

  /**
   * This instance pins only (Root + OPERATING / OVERDRAFT / PROGRAM).
   * `/programs` omits rootAccountId — resolve pin from transfer-options sources.
   * Peer Conta Digitals on matrix AccInst stay on Contas (never INFRA shells).
   */
  const operationShellAccounts = useMemo(() => {
    const instanceRootId = resolveRootPageRootAccountId({
      rootAccountId: selectedInstance?.rootAccountId,
      sourceAccounts: transferSourceAccounts,
    });
    const byId = new Map();
    for (const account of [
      ...(transferSourceAccounts || []),
      ...(transferDestinationAccounts || []),
      ...(transferVirtualAccounts || []),
    ]) {
      if (!isRootPageOperationShell(account, { rootAccountId: instanceRootId })) {
        continue;
      }
      const id = String(account?.id || "").trim();
      if (!id || byId.has(id)) continue;
      byId.set(id, account);
    }
    return Array.from(byId.values());
  }, [
    transferSourceAccounts,
    transferDestinationAccounts,
    transferVirtualAccounts,
    selectedInstance?.rootAccountId,
  ]);

  const shellFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return String(params.get("shell") || "").trim();
  }, [location.search]);

  useEffect(() => {
    if (transferOptionsLoading) return;
    const instanceRootId = resolveRootPageRootAccountId({
      rootAccountId: selectedInstance?.rootAccountId,
      sourceAccounts: transferSourceAccounts,
    });
    setSelectedShellId((prev) => {
      const ids = new Set(
        operationShellAccounts.map((s) => String(s?.id || "").trim()).filter(Boolean),
      );
      if (shellFromQuery && ids.has(shellFromQuery)) return shellFromQuery;
      if (prev && ids.has(prev)) return prev;
      return pickDefaultRootShellId(operationShellAccounts, {
        rootAccountId: instanceRootId,
      });
    });
  }, [
    transferOptionsLoading,
    operationShellAccounts,
    transferSourceAccounts,
    selectedInstance?.rootAccountId,
    shellFromQuery,
    dataEpoch,
  ]);

  const syncShellQuery = (accountId) => {
    const id = String(accountId || "").trim();
    const params = new URLSearchParams(location.search);
    if (id) params.set("shell", id);
    else params.delete("shell");
    const search = params.toString();
    history.replace({
      pathname: BAAS_ROOT_PATH,
      search: search ? `?${search}` : "",
    });
    setSelectedShellId(id);
  };

  const credentialLoadError = useMemo(
    () => formatBaasInstanceCredentialError(transferOptionsError),
    [transferOptionsError],
  );

  const showProdAwaitingBanner = isBaasProdAwaitingLiberation(selectedInstance);

  // One red credential banner only — shells panel reuses the same message when no top banner.
  const ownerErrorText = ownerError
    ? String(
        typeof ownerError === "string"
          ? ownerError
          : ownerError.detail || ownerError.message || ownerError,
      ).trim()
    : "";
  const credentialErrorText = credentialLoadError
    ? String(
        credentialLoadError.detail
          || credentialLoadError.message
          || "",
      ).trim()
    : "";
  const showOwnerCredentialError =
    Boolean(ownerErrorText)
    && (!credentialErrorText
      || ownerErrorText.toLowerCase() !== credentialErrorText.toLowerCase());
  const showTopCredentialError = Boolean(credentialErrorText) && !showProdAwaitingBanner;
  // Never stack a second red error under Shells when prod banner or top credential already shown.
  const shellsPanelError =
    showProdAwaitingBanner || showTopCredentialError ? null : transferOptionsError;

  if (isLoading || capabilitiesLoading) {
    return <BaasTablePageSkeleton withQuickActions columns={4} />;
  }

  if (!hasBaasBankingRails(capabilities) && !isBaasCardsOnlyInstance(capabilities)) {
    return <Redirect to="/baas/inicio" />;
  }

  if (!canViewAccounts && !canViewEntities) {
    return (
      <div className="baas-root min-w-0 space-y-5">
        <header>
          <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.nav.root")}
          </h1>
        </header>
        <div className="app-card px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.home.capabilities.permissionDenied")}
        </div>
      </div>
    );
  }

  const ownerReadOnly = !isValutConfigAdmin;
  const ownerDetailPath = ownerEntityId
    ? buildEntidadeDetailPath(ownerEntityId)
    : null;

  return (
    <div className="baas-root min-w-0 space-y-5">
      <header className="min-w-0 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-[#e7a73f]">
            {intl.get("baas.root.eyebrow")}
          </p>
          <h1 className="mt-1 text-2xl font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.nav.root")}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.root.subtitle")}
          </p>
        </div>
        {canTransfer ? (
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-2 self-start">
            <BaasFundShellsButton className="btn-primary !h-9 !text-xs shrink-0" />
          </div>
        ) : null}
      </header>

      {showProdAwaitingBanner ? (
        <BaasProdAwaitingHomologBanner
          instance={selectedInstance}
          instances={instances}
        />
      ) : null}

      <section className="min-w-0 space-y-2">
        <div>
          <p className="dashboard-section-label mb-0.5">
            {intl.get("baas.root.owner.section")}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.root.owner.subtitle")}
          </p>
        </div>

        {ownerLoading ? (
          <div className="app-card px-4 py-5 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.root.owner.loading")}
          </div>
        ) : null}

        {!ownerLoading && showOwnerCredentialError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
            {ownerErrorText}
          </div>
        ) : null}

        {!ownerLoading && !ownerEntity && ownerEntityId ? (
          <div className="summary-card flex min-w-0 flex-col gap-3 !bg-white !p-4 dark:!border-brand-dark-border dark:!bg-brand-dark-elevated sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[rgba(231,167,63,0.15)] text-[#a66f1d]">
                <TbShield size={20} aria-hidden />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
                    {ownerEntityId}
                  </p>
                  <span className="inline-flex rounded-full bg-[#fff8f0] px-2 py-0.5 text-[11px] font-semibold text-[#9a7340] dark:bg-[#2a2a2a] dark:text-brand-gold-mid">
                    {intl.get("baas.root.owner.badge")}
                  </span>
                </div>
                {showOwnerCredentialError ? null : (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {intl.get("baas.root.owner.detailUnavailable")}
                  </p>
                )}
              </div>
            </div>
            {ownerDetailPath ? (
              <Link
                to={ownerDetailPath}
                className="btn-secondary inline-flex !h-9 w-auto shrink-0 items-center gap-1.5 !text-xs self-start"
              >
                <TbExternalLink size={14} aria-hidden />
                {intl.get("baas.root.owner.openEntity")}
              </Link>
            ) : null}
          </div>
        ) : null}

        {!ownerLoading && !ownerError && !ownerEntityId ? (
          <div className="app-card px-4 py-5 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.root.owner.empty")}
          </div>
        ) : null}

        {!ownerLoading && ownerEntity ? (
          <div className="summary-card flex min-w-0 flex-col gap-3 !bg-white !p-4 dark:!border-brand-dark-border dark:!bg-brand-dark-elevated sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[rgba(231,167,63,0.15)] text-[#a66f1d]">
                <TbShield size={20} aria-hidden />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
                    {ownerEntity.name || ownerEntityId}
                  </p>
                  <span className="inline-flex rounded-full bg-[#fff8f0] px-2 py-0.5 text-[11px] font-semibold text-[#9a7340] dark:bg-[#2a2a2a] dark:text-brand-gold-mid">
                    {intl.get("baas.root.owner.badge")}
                  </span>
                  {ownerEntity.type === "pf" || ownerEntity.type === "pj" ? (
                    <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400">
                      {intl.get(`baas.entidades.type.${ownerEntity.type}`)}
                    </span>
                  ) : null}
                  {ownerReadOnly ? (
                    <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400">
                      {intl.get("baas.root.owner.readOnlyBadge")}
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 font-mono text-xs text-gray-500 dark:text-gray-400">
                  {ownerEntity.id || ownerEntityId}
                </p>
                <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">
                  {intl.get("baas.root.owner.document")}: {formatDocument(ownerEntity.document)}
                </p>
              </div>
            </div>
            {ownerDetailPath ? (
              <Link
                to={ownerDetailPath}
                className="btn-secondary inline-flex !h-9 w-auto shrink-0 items-center gap-1.5 !text-xs self-start"
              >
                <TbExternalLink size={14} aria-hidden />
                {intl.get("baas.root.owner.openEntity")}
              </Link>
            ) : null}
          </div>
        ) : null}
      </section>

      {showTopCredentialError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
          {credentialErrorText}
        </div>
      ) : null}

      {canViewAccounts ? (
        <>
          <BaasEntityVirtualAccountsPanel
            variant="operation"
            shellAccounts={operationShellAccounts}
            loading={transferOptionsLoading}
            error={shellsPanelError}
            selectedAccountId={selectedShellId}
            onSelectAccount={(account) => syncShellQuery(account?.id)}
          />

          {selectedShellId ? (
            <section className="min-w-0 space-y-2 border-t border-gray-100 pt-3 dark:border-brand-dark-border">
              <div>
                <p className="dashboard-section-label mb-0.5">
                  {intl.get("baas.root.shellDetail.section")}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {intl.get("baas.root.shellDetail.subtitle")}
                </p>
              </div>
              <BaasAccountDetailPanel
                key={selectedShellId}
                accountId={selectedShellId}
                embedded
                showBackLink={false}
              />
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default BaasRootPage;
